/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { organsAPI, meclisBaskaniAPI } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building, Plus, Edit, Trash2, Users, Calendar, Power,
  ChevronDown, ChevronRight, ChevronLeft, Loader2, Shield, Briefcase,
  Network, UserPlus, X, Search, Crown, UserMinus, UserCog, Download, Copy,
} from 'lucide-react';

const TC = {
  donem:                    { label: 'Donem',                    dot: '#3b82f6', bg: 'bg-blue-600',    lt: 'bg-blue-50 text-blue-700 border-blue-200',     ring: 'ring-blue-400' },
  genel_kurul:              { label: 'Genel Kurul',              dot: '#8b5cf6', bg: 'bg-violet-600',  lt: 'bg-violet-50 text-violet-700 border-violet-200', ring: 'ring-violet-400' },
  yurutme_kurulu:           { label: 'Yürütme Kurulu',           dot: '#10b981', bg: 'bg-emerald-600', lt: 'bg-emerald-50 text-emerald-700 border-emerald-200', ring: 'ring-emerald-400' },
  meclis:                   { label: 'Meclis',                   dot: '#f59e0b', bg: 'bg-amber-600',   lt: 'bg-amber-50 text-amber-700 border-amber-200',   ring: 'ring-amber-400' },
  calisma_grubu:            { label: 'Çalışma Grubu',            dot: '#6366f1', bg: 'bg-indigo-600',  lt: 'bg-indigo-50 text-indigo-700 border-indigo-200', ring: 'ring-indigo-400' },
  kent_konseyi_baskani:     { label: 'Kent Konseyi Başkanı',     dot: '#dc2626', bg: 'bg-red-700',     lt: 'bg-red-50 text-red-700 border-red-200',         ring: 'ring-red-400' },
  genel_sekreter:           { label: 'Genel Sekreter',           dot: '#0891b2', bg: 'bg-cyan-700',    lt: 'bg-cyan-50 text-cyan-700 border-cyan-200',       ring: 'ring-cyan-400' },
  genel_sekreter_yardimcisi:{ label: 'Genel Sekreter Yrd.',      dot: '#0d9488', bg: 'bg-teal-600',    lt: 'bg-teal-50 text-teal-700 border-teal-200',       ring: 'ring-teal-400' },
};
const TI = { donem: Calendar, genel_kurul: Users, yurutme_kurulu: Shield, meclis: Building, calisma_grubu: Briefcase, kent_konseyi_baskani: Crown, genel_sekreter: UserCog, genel_sekreter_yardimcisi: UserCog };
const PM = { donem: null, genel_kurul: ['donem'], yurutme_kurulu: ['donem', 'meclis'], meclis: ['donem'], calisma_grubu: ['meclis'], kent_konseyi_baskani: ['donem'], genel_sekreter: ['donem'], genel_sekreter_yardimcisi: ['donem'] };
const TK = ['donem', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi', 'genel_kurul', 'yurutme_kurulu', 'meclis', 'calisma_grubu'];
function childTypes(t) { return TK.filter(k => { const p = PM[k]; return Array.isArray(p) ? p.includes(t) : p === t; }); }

function flatVis(nodes, depth, col) {
  const r = [];
  for (const n of nodes) {
    r.push({ ...n, _d: depth });
    if (n.children && n.children.length > 0 && !col[n.id]) r.push(...flatVis(n.children, depth + 1, col));
  }
  return r;
}

function findNodeById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) { const found = findNodeById(n.children, id); if (found) return found; }
  }
  return null;
}

// Donem secildiginde ust duzey atamalari gosteren panel
function DonemKadroPanel({ detail, canEdit, onAssign, onRemove, onSelect, organsAPI }) {
  const [kadro, setKadro] = useState({});
  const [loading, setLoading] = useState(true);

  const POZISYONLAR = [
    { type: 'kent_konseyi_baskani', label: 'Kent Konseyi Baskani', icon: Crown, color: 'bg-red-100 text-red-700' },
    { type: 'genel_sekreter', label: 'Genel Sekreter', icon: UserCog, color: 'bg-cyan-100 text-cyan-700' },
    { type: 'genel_sekreter_yardimcisi', label: 'Genel Sekreter Yrd.', icon: UserCog, color: 'bg-teal-100 text-teal-700' },
    { type: 'yurutme_kurulu', label: 'Yurutme Kurulu Baskani', icon: Shield, color: 'bg-emerald-100 text-emerald-700' },
  ];

  useEffect(() => {
    const loadKadro = async () => {
      setLoading(true);
      const result = {};
      const children = detail.children || [];
      for (const poz of POZISYONLAR) {
        const organ = children.find(c => c.type === poz.type);
        if (organ) {
          try {
            const r = await organsAPI.getMembers(organ.id);
            result[poz.type] = { organ, baskan: r.data?.baskan || null, members: r.data?.members || [] };
          } catch {
            result[poz.type] = { organ, baskan: null, members: [] };
          }
        }
      }
      setKadro(result);
      setLoading(false);
    };
    loadKadro();
  }, [detail.id, detail.children]);

  if (loading) return (
    <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
  );

  const hasPoz = POZISYONLAR.some(p => kadro[p.type]);
  if (!hasPoz) return null;

  return (
    <div data-testid="donem-kadro-panel">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
        <Crown className="w-3.5 h-3.5" /> Donem Kadrosu
      </h3>
      <div className="space-y-2">
        {POZISYONLAR.map(poz => {
          const data = kadro[poz.type];
          if (!data) return null;
          const Icon = poz.icon;
          return (
            <div key={poz.type} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50/50 transition-colors" data-testid={`kadro-${poz.type}`}>
              <div className={`w-8 h-8 rounded-lg ${poz.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">{poz.label}</p>
                {data.baskan ? (
                  <p className="text-sm font-medium text-slate-900 truncate">{data.baskan.name} {data.baskan.surname}</p>
                ) : (
                  <p className="text-xs text-slate-400 italic">Atanmamis</p>
                )}
              </div>
              {data.baskan && (
                <Badge className="bg-green-50 text-green-700 border-green-200 border text-[10px] flex-shrink-0">Aktif</Badge>
              )}
              <div className="flex gap-1 flex-shrink-0">
                {canEdit && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600 px-2"
                    onClick={() => onAssign(data.organ.id)} data-testid={`assign-${poz.type}`}>
                    <UserPlus className="w-3 h-3 mr-1" />{data.baskan ? 'Degistir' : 'Ata'}
                  </Button>
                )}
                {canEdit && data.baskan && (
                  <button onClick={() => onRemove(data.organ.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-${poz.type}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* YK Uyeleri */}
      {kadro['yurutme_kurulu'] && kadro['yurutme_kurulu'].members.length > 0 && (
        <div className="mt-3">
          <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Users className="w-3 h-3" /> Yurutme Kurulu Uyeleri ({kadro['yurutme_kurulu'].members.length})
          </h4>
          <div className="space-y-1">
            {kadro['yurutme_kurulu'].members.map(m => (
              <div key={m.id} className="flex items-center gap-2 py-1 px-2 rounded text-xs text-slate-600">
                <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[9px] font-bold">{m.name?.[0]}{m.surname?.[0]}</div>
                <span className="truncate">{m.name} {m.surname}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrgansPage() {
  const { hasPermission } = useAuth();
  usePageTitle('Organ Yapisi');
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [col, setCol] = useState({});
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [members, setMembers] = useState({ baskan: null, members: [] });
  // Onaylanmis uye listesi icin arama + sayfalama (binlerce uye senaryosu)
  const [memSearch, setMemSearch] = useState('');
  const [memPage, setMemPage] = useState(1);
  const MEM_PAGE_SIZE = 20;
  const [dOpen, setDOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'donem', parent_id: '', description: '', start_date: '', end_date: '' });
  const [cOpts, setCOpts] = useState([]);
  // User search
  const [uOpen, setUOpen] = useState(false);
  const [uMode, setUMode] = useState('baskan'); // 'baskan' | 'member' | 'approved'
  const [uSearch, setUSearch] = useState('');
  const [uResults, setUResults] = useState([]);
  const [uLoading, setULoading] = useState(false);
  const searchTimer = useRef(null);

  const canCreate = hasPermission('organs.create');
  const canEdit = hasPermission('organs.edit');
  const canDelete = hasPermission('organs.delete');

  const load = useCallback(async () => {
    try {
      // Admin/editorler pasif donemleri de gorebilsin
      const params = canEdit ? { include_inactive: true } : {};
      const [t, f] = await Promise.all([organsAPI.getTree(params), organsAPI.getAll(params)]);
      setTree(t.data || []);
      setFlat(f.data || []);
    } catch { toast.error('Yuklenirken hata olustu'); }
    finally { setLoading(false); }
  }, [canEdit]);

  useEffect(() => { load(); }, [load]);

  const loadMembers = useCallback(async (id) => {
    try {
      const r = await organsAPI.getMembers(id);
      setMembers(r.data || { baskan: null, members: [], meclis_uyeleri: [] });
    } catch { setMembers({ baskan: null, members: [], meclis_uyeleri: [] }); }
  }, []);

  const selectNode = useCallback((node) => {
    setSel(node.id);
    setDetail(node);
    setMemSearch('');
    setMemPage(1);
    loadMembers(node.id);
  }, [loadMembers]);

  const donemTrees = tree.filter(n => n.type === 'donem');
  const orphans = tree.filter(n => n.type !== 'donem');
  const vis = [...flatVis(donemTrees, 0, col), ...flatVis(orphans, 0, col)];
  const activeDonem = flat.find(o => o.type === 'donem' && o.is_active);
  const allDonemler = flat.filter(o => o.type === 'donem').sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0) || a.name.localeCompare(b.name));

  // Export donem selector
  const [exportOpen, setExportOpen] = useState(false);
  const [exportDonemId, setExportDonemId] = useState('');

  const toggleCol = (id) => setCol(p => ({ ...p, [id]: !p[id] }));

  // CRUD
  const openNew = (type, parent_id) => { setEditing(null); setCOpts(type ? [type] : []); setForm({ name: '', type: type || 'donem', parent_id: parent_id || '', description: '', start_date: '', end_date: '' }); setDOpen(true); };
  const openEdit = (o) => { setEditing(o); setCOpts([]); setForm({ name: o.name, type: o.type, parent_id: o.parent_id || '', description: o.description || '', start_date: o.start_date || '', end_date: o.end_date || '' }); setDOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Ad zorunlu'); return; }
    setSaving(true);
    try {
      const p = { ...form }; if (!p.parent_id) delete p.parent_id;
      if (editing) { await organsAPI.update(editing.id, p); toast.success('Guncellendi'); }
      else { await organsAPI.create(p); toast.success('Olusturuldu'); }
      setDOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDel = async (o) => {
    if (!window.confirm(`"${o.name}" silinsin mi?`)) return;
    try { await organsAPI.delete(o.id); toast.success('Silindi'); if (sel === o.id) { setSel(null); setDetail(null); } load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hata'); }
  };

  const handleToggle = async (o) => {
    const a = o.is_active ? 'pasife al' : 'aktif et';
    if (!window.confirm(`"${o.name}" ${a}?`)) return;
    try { await organsAPI.toggleActive(o.id); toast.success(o.is_active ? 'Pasif' : 'Aktif'); load(); }
    catch (e) { toast.error('Hata'); }
  };

  // Donem Kopyalama
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyForm, setCopyForm] = useState({ new_name: '', start_date: '', end_date: '', copy_members: true, copy_approved_members: true, activate: false, copy_meetings: false, copy_meeting_attendees: false, copy_announcements: false, copy_announcements_active: false, copy_surveys: false, copy_survey_responses: false, copy_events: false, copy_event_applications: false });
  const [copySaving, setCopySaving] = useState(false);

  const openCopyDonem = (d) => {
    setCopySource(d);
    const defaultName = d.name ? `${d.name} (Kopya)` : 'Yeni Donem';
    setCopyForm({ new_name: defaultName, start_date: '', end_date: '', copy_members: true, copy_approved_members: true, activate: false, copy_meetings: false, copy_meeting_attendees: false, copy_announcements: false, copy_announcements_active: false, copy_surveys: false, copy_survey_responses: false, copy_events: false, copy_event_applications: false });
    setCopyOpen(true);
  };

  const handleCopyDonem = async () => {
    if (!copyForm.new_name?.trim()) { toast.error('Yeni donem adi zorunlu'); return; }
    setCopySaving(true);
    try {
      const r = await organsAPI.copyDonem({
        source_donem_id: copySource.id,
        new_name: copyForm.new_name.trim(),
        start_date: copyForm.start_date || null,
        end_date: copyForm.end_date || null,
        copy_members: copyForm.copy_members,
        copy_approved_members: copyForm.copy_approved_members,
        activate: copyForm.activate,
        copy_meetings: copyForm.copy_meetings,
        copy_meeting_attendees: copyForm.copy_meetings && copyForm.copy_meeting_attendees,
        copy_announcements: copyForm.copy_announcements,
        copy_announcements_active: copyForm.copy_announcements && copyForm.copy_announcements_active,
        copy_surveys: copyForm.copy_surveys,
        copy_survey_responses: copyForm.copy_surveys && copyForm.copy_survey_responses,
        copy_events: copyForm.copy_events,
        copy_event_applications: copyForm.copy_events && copyForm.copy_event_applications,
      });
      const c = r.data.copied_counts || {};
      const cc = r.data.copied_content || {};
      const contentMsg = (cc.meetings || cc.announcements || cc.surveys || cc.events)
        ? `, ${cc.meetings || 0} toplanti, ${cc.announcements || 0} duyuru, ${cc.surveys || 0} anket, ${cc.events || 0} etkinlik`
        : '';
      toast.success(`Kopyalandi: ${c.meclis || 0} meclis, ${c.calisma_grubu || 0} CG, ${r.data.copied_approved_members || 0} uye${contentMsg}`);
      setCopyOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Kopyalama basarisiz');
    } finally { setCopySaving(false); }
  };

  // User search for baskan/member
  const searchUsers = (q) => {
    setUSearch(q);
    clearTimeout(searchTimer.current);
    if (q.length < 2) { setUResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setULoading(true);
      try { const r = await organsAPI.searchUsers(q); setUResults(r.data || []); }
      catch { setUResults([]); }
      finally { setULoading(false); }
    }, 300);
  };

  // Referans: atama oncesi donem ID'si (kadro panelden atama yaparken geri donmek icin)
  const [returnToDonem, setReturnToDonem] = useState(null);

  const roleLabelForOrgan = (o) => {
    if (!o) return 'Baskan';
    switch (o.type) {
      case 'yurutme_kurulu': return 'Yürütme Kurulu Başkanı';
      case 'meclis': return 'Meclis Başkanı';
      case 'calisma_grubu': return 'Grup Başkanı';
      case 'kent_konseyi_baskani': return 'Kent Konseyi Başkanı';
      case 'genel_sekreter': return 'Genel Sekreter';
      case 'genel_sekreter_yardimcisi': return 'Genel Sekreter Yardımcısı';
      default: return 'Başkan';
    }
  };

  const assignBaskan = async (userId) => {
    if (!detail) return;
    try {
      await organsAPI.setBaskan(detail.id, userId);
      toast.success(`${roleLabelForOrgan(detail)} atandi`);
      setUOpen(false);
      await load();
      // Kadro panelinden geldiyse doneme geri don
      if (returnToDonem) {
        const [t2] = await Promise.all([organsAPI.getTree()]);
        const donemNode = findNodeById(t2.data || [], returnToDonem);
        if (donemNode) { selectNode(donemNode); }
        setReturnToDonem(null);
      } else {
        loadMembers(detail.id);
      }
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hata'); }
  };

  const removeBaskan = async () => {
    if (!detail) return;
    try { await organsAPI.setBaskan(detail.id, ''); toast.success(`${roleLabelForOrgan(detail)} kaldirildi`); loadMembers(detail.id); load(); }
    catch (e) { toast.error('Hata'); }
  };

  const addMember = async (userId) => {
    if (!detail) return;
    try { await organsAPI.addMember(detail.id, userId); toast.success('Uye eklendi'); setUOpen(false); loadMembers(detail.id); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hata'); }
  };

  const addApprovedMember = async (userId) => {
    if (!detail) return;
    try { await organsAPI.addApprovedMember(detail.id, userId); toast.success('Uye eklendi'); setUOpen(false); loadMembers(detail.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hata'); }
  };

  const removeMember = async (userId) => {
    if (!detail) return;
    try { await organsAPI.removeMember(detail.id, userId); toast.success('Uye cikarildi'); loadMembers(detail.id); load(); }
    catch (e) { toast.error('Hata'); }
  };

  const parentOpts = () => { const pt = PM[form.type]; if (!pt) return []; return flat.filter(o => Array.isArray(pt) ? pt.includes(o.type) : o.type === pt); };
  const typeOpts = cOpts.length > 0 ? cOpts : TK;

  if (loading) return <div className="flex items-center justify-center h-[70vh]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const dCfg = detail ? (TC[detail.type] || TC.meclis) : null;
  const DIcon = detail ? (TI[detail.type] || Building) : Building;

  return (
    <div className="space-y-5" data-testid="organs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center"><Network className="w-4 h-4 text-white" /></div>
            Organ Yapisi
          </h1>
          {activeDonem && <p className="text-xs text-slate-500 mt-1 ml-[42px] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />{activeDonem.name}</p>}
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button size="sm" variant="outline" onClick={() => { setExportDonemId(activeDonem?.id || ''); setExportOpen(true); }} data-testid="export-all-btn" className="text-xs h-8 text-emerald-700 border-slate-200 hover:bg-emerald-50">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Tum Meclisleri Excel'e Aktar
            </Button>
          )}
          {canCreate && <Button size="sm" onClick={() => openNew('donem', '')} data-testid="new-donem-btn" className="bg-slate-900 hover:bg-slate-800 text-xs h-8"><Plus className="w-3.5 h-3.5 mr-1.5" />Yeni Donem</Button>}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TK.map(k => { const I = TI[k]; const c = TC[k]; const cnt = flat.filter(o => o.type === k).length; return (
          <div key={k} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white min-w-fit" data-testid={`stat-${k}`}>
            <div className={`w-6 h-6 rounded ${c.bg} flex items-center justify-center`}><I className="w-3 h-3 text-white" /></div>
            <span className="text-xs text-slate-500">{c.label}</span>
            <span className="text-sm font-bold text-slate-900">{cnt}</span>
          </div>
        ); })}
      </div>

      {/* Master-Detail Layout */}
      <div className="grid grid-cols-12 gap-4 min-h-[60vh]">
        {/* Left: Tree */}
        <div className="col-span-12 lg:col-span-5 border border-slate-200 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{flat.length} Organ</span>
          </div>
          <div className="overflow-y-auto max-h-[calc(70vh-120px)] p-2">
            {vis.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <Network className="w-10 h-10 text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Henuz organ yok</p>
                {canCreate && <Button size="sm" onClick={() => openNew('donem', '')} className="mt-3 text-xs bg-slate-900">Ilk Donem</Button>}
              </div>
            ) : vis.map(item => {
              const cfg = TC[item.type] || TC.meclis;
              const hasKids = item.children && item.children.length > 0;
              const isC = col[item.id];
              const isSel = sel === item.id;
              const cts = childTypes(item.type);

              return (
                <div key={item.id} data-testid={`tree-node-${item.id}`}
                  className={`group flex items-center gap-1.5 py-2 px-2 rounded-lg cursor-pointer transition-all mb-0.5 ${isSel ? 'bg-slate-900 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
                  style={{ paddingLeft: 8 + item._d * 20 }}
                  onClick={() => selectNode(item)}>

                  <button onClick={(e) => { e.stopPropagation(); toggleCol(item.id); }}
                    className={`w-4 h-4 flex items-center justify-center rounded flex-shrink-0 ${hasKids ? (isSel ? 'text-slate-300' : 'text-slate-400') : 'text-transparent'}`}>
                    {hasKids && (isC ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </button>

                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.dot }} />
                  <span className={`text-xs font-medium flex-1 truncate ${isSel ? 'text-white' : ''} ${item.is_active === false ? 'opacity-50 line-through decoration-red-300' : ''}`}>{item.name}</span>
                  {hasKids && <span className={`text-[9px] rounded-full px-1 ${isSel ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-400'}`}>{item.children.length}</span>}

                  {item.type === 'donem' && <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.is_active ? 'bg-green-400' : 'bg-red-400'}`} title={item.is_active ? 'Aktif' : 'Pasif'} />}

                  {/* Hover actions */}
                  <div className={`flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${isSel ? 'opacity-100' : ''}`} onClick={e => e.stopPropagation()}>
                    {canCreate && cts.length > 0 && <button onClick={() => openNew(cts[0], item.id)} className={`w-5 h-5 rounded flex items-center justify-center ${isSel ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-blue-50 text-blue-500'}`} data-testid={`add-child-${item.id}`}><Plus className="w-3 h-3" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div className="col-span-12 lg:col-span-7 border border-slate-200 rounded-xl bg-white overflow-hidden">
          {!detail ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Network className="w-6 h-6 text-slate-300" /></div>
              <p className="text-sm text-slate-400">Detay gormek icin sol taraftan bir organ secin</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Detail Header */}
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl ${dCfg.bg} flex items-center justify-center flex-shrink-0`}>
                      <DIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base font-bold text-slate-900 truncate">{detail.name}</h2>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`${dCfg.lt} border text-[10px] px-1.5 py-0`}>{dCfg.label}</Badge>
                        {detail.type === 'donem' && <Badge className={`text-[10px] px-1.5 py-0 border ${detail.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>{detail.is_active ? 'Aktif' : 'Pasif'}</Badge>}
                        {detail.type === 'donem' && detail.start_date && <span className="text-[10px] text-slate-400">{detail.start_date} - {detail.end_date || '?'}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {['donem','meclis','calisma_grubu','yurutme_kurulu'].includes(detail.type) && canEdit && <Button variant="outline" size="sm" className={`h-7 text-xs ${detail.is_active ? '' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`} onClick={() => handleToggle(detail)} data-testid="toggle-active-btn"><Power className="w-3 h-3 mr-1" />{detail.is_active ? 'Pasif Yap' : 'Aktif Yap'}</Button>}
                    {detail.type === 'donem' && canCreate && <Button variant="outline" size="sm" className="h-7 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => openCopyDonem(detail)} data-testid="copy-donem-btn"><Copy className="w-3 h-3 mr-1" />Kopyala</Button>}
                    {canEdit && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openEdit(detail)} data-testid="edit-detail-btn"><Edit className="w-3 h-3 mr-1" />Duzenle</Button>}
                    {canDelete && <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDel(detail)} data-testid="delete-detail-btn"><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                </div>
                {detail.description && <p className="text-xs text-slate-500 mt-2 ml-[52px]">{detail.description}</p>}
              </div>

              {/* Detail Body */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5">

                {/* Donem Kadrosu - donem secildiginde ust duzeydeki atamalari goster */}
                {detail.type === 'donem' && detail.children && (
                  <DonemKadroPanel
                    detail={detail}
                    canEdit={canEdit}
                    onAssign={(organId) => {
                      const childOrgan = detail.children.find(c => c.id === organId);
                      if (childOrgan) {
                        setReturnToDonem(detail.id);
                        setDetail(childOrgan);
                        setSel(childOrgan.id);
                        loadMembers(childOrgan.id);
                        setUMode('baskan');
                        setUSearch('');
                        setUResults([]);
                        setUOpen(true);
                      }
                    }}
                    onRemove={async (organId) => {
                      try {
                        await organsAPI.setBaskan(organId, '');
                        toast.success('Gorev sonlandirildi, kullanici Vatandas olarak guncellendi');
                        await load();
                        // Donemi yeniden yukle
                        const [t2] = await Promise.all([organsAPI.getTree()]);
                        const donemNode = findNodeById(t2.data || [], detail.id);
                        if (donemNode) { selectNode(donemNode); }
                      } catch (e) { toast.error('Hata'); }
                    }}
                    onSelect={(node) => selectNode(node)}
                    organsAPI={organsAPI}
                  />
                )}
                {/* Baskan Section - for meclis, calisma_grubu and new types */}
                {(detail.type === 'meclis' || detail.type === 'calisma_grubu' || detail.type === 'kent_konseyi_baskani' || detail.type === 'genel_sekreter' || detail.type === 'genel_sekreter_yardimcisi' || detail.type === 'yurutme_kurulu') && (
                  <div data-testid="baskan-section">
                    <div className="flex items-center justify-between mb-2.5">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" />{detail.type === 'yurutme_kurulu' ? 'Yürütme Kurulu Başkanı' : detail.type === 'meclis' ? 'Meclis Baskani' : detail.type === 'calisma_grubu' ? 'Grup Baskani' : detail.type === 'kent_konseyi_baskani' ? 'Kent Konseyi Baskani' : detail.type === 'genel_sekreter' ? 'Genel Sekreter' : 'Genel Sekreter Yardimcisi'}</h3>
                      {canEdit && <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => { setUMode('baskan'); setUSearch(''); setUResults([]); setUOpen(true); }} data-testid="assign-baskan-btn"><UserPlus className="w-3 h-3 mr-1" />Ata</Button>}
                    </div>
                    {members.baskan ? (
                      <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">{members.baskan.name?.[0]}{members.baskan.surname?.[0]}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{members.baskan.name} {members.baskan.surname}</p>
                          <p className="text-[10px] text-slate-400">{members.baskan.email}</p>
                        </div>
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]">{
                          detail.type === 'yurutme_kurulu' ? 'Yürütme Kurulu Başkanı'
                            : detail.type === 'meclis' ? 'Meclis Başkanı'
                            : detail.type === 'calisma_grubu' ? 'Grup Başkanı'
                            : detail.type === 'kent_konseyi_baskani' ? 'Kent Konseyi Başkanı'
                            : detail.type === 'genel_sekreter' ? 'Genel Sekreter'
                            : detail.type === 'genel_sekreter_yardimcisi' ? 'Genel Sekreter Yardımcısı'
                            : 'Başkan'
                        }</Badge>
                        {canEdit && <button onClick={removeBaskan} className="text-red-400 hover:text-red-600 p-1" data-testid="remove-baskan-btn"><X className="w-3.5 h-3.5" /></button>}
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg border border-dashed border-slate-200 text-center">
                        <p className="text-xs text-slate-400">
                          {detail.type === 'yurutme_kurulu' ? 'Henüz Yürütme Kurulu Başkanı atanmamış'
                            : detail.type === 'meclis' ? 'Henüz Meclis Başkanı atanmamış'
                            : detail.type === 'calisma_grubu' ? 'Henüz Grup Başkanı atanmamış'
                            : detail.type === 'kent_konseyi_baskani' ? 'Henüz Kent Konseyi Başkanı atanmamış'
                            : detail.type === 'genel_sekreter' ? 'Henüz Genel Sekreter atanmamış'
                            : detail.type === 'genel_sekreter_yardimcisi' ? 'Henüz Genel Sekreter Yardımcısı atanmamış'
                            : 'Henüz başkan atanmamış'}
                        </p>
                      </div>
                    )}
                    {detail.type === 'yurutme_kurulu' && (
                      <p className="text-[10px] text-blue-500 mt-1.5">Atanan kullanıcının rolü otomatik olarak "Yürütme Kurulu Başkanı" olarak güncellenecektir.</p>
                    )}
                  </div>
                )}

                {/* Members Section */}
                {(detail.type === 'yurutme_kurulu' || detail.type === 'genel_kurul' || detail.type === 'meclis' || detail.type === 'calisma_grubu') && (
                  <div data-testid="members-section">
                    {/* YK Uyeleri (organ.members) */}
                    <div className="flex items-center justify-between mb-2.5">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" />
                        {detail.type === 'meclis' ? 'Yurutme Kurulu Uyeleri' : 'Uyeler'} ({members.members.length})
                      </h3>
                      {canEdit && <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => { setUMode('member'); setUSearch(''); setUResults([]); setUOpen(true); }} data-testid="add-member-btn"><UserPlus className="w-3 h-3 mr-1" />{detail.type === 'meclis' ? 'YK Uyesi Ekle' : 'Uye Ekle'}</Button>}
                    </div>
                    {members.members.length > 0 ? (
                      <div className="space-y-1">
                        {members.members.map(m => (
                          <div key={m.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 group transition-colors">
                            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-[10px] font-bold">{m.name?.[0]}{m.surname?.[0]}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-800 truncate">{m.name} {m.surname}</p>
                              <p className="text-[10px] text-slate-400">{m.email}</p>
                            </div>
                            {detail.type === 'meclis' && <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[9px]">YK</Badge>}
                            {canEdit && <button onClick={() => removeMember(m.id)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 p-1 transition-opacity" data-testid={`remove-member-${m.id}`}><UserMinus className="w-3 h-3" /></button>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg border border-dashed border-slate-200 text-center">
                        <p className="text-xs text-slate-400">{detail.type === 'meclis' ? 'Henuz YK uyesi eklenmemis' : 'Henuz uye eklenmemis'}</p>
                      </div>
                    )}

                    {/* Onaylanmis Uyeler (basvuru ile onaylananlar - meclis ve CG) */}
                    {(detail.type === 'meclis' || detail.type === 'calisma_grubu') && (() => {
                      const fullList = members.meclis_uyeleri || [];
                      const q = memSearch.trim().toLowerCase();
                      const filtered = q
                        ? fullList.filter(m =>
                            (m.name || '').toLowerCase().includes(q) ||
                            (m.surname || '').toLowerCase().includes(q) ||
                            (m.email || '').toLowerCase().includes(q))
                        : fullList;
                      const totalPages = Math.max(1, Math.ceil(filtered.length / MEM_PAGE_SIZE));
                      const safePage = Math.min(memPage, totalPages);
                      const start = (safePage - 1) * MEM_PAGE_SIZE;
                      const pageItems = filtered.slice(start, start + MEM_PAGE_SIZE);
                      return (
                      <div className="mt-4" data-testid="approved-uyeleri-section">
                        <div className="flex items-center justify-between mb-2.5">
                          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            {detail.type === 'meclis' ? 'Meclis' : 'CG'} Uyeleri ({fullList.length}{q ? ` / ${filtered.length} eslesme` : ''})
                          </h3>
                          {canEdit && <Button variant="ghost" size="sm" className="h-6 text-xs text-green-600" onClick={() => { setUMode('approved'); setUSearch(''); setUResults([]); setUOpen(true); }} data-testid="add-approved-member-btn"><UserPlus className="w-3 h-3 mr-1" />Uye Ekle</Button>}
                        </div>
                        {fullList.length > MEM_PAGE_SIZE / 2 && (
                          <div className="relative mb-2">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Uye ara (ad, soyad, e-posta)..."
                              value={memSearch}
                              onChange={(e) => { setMemSearch(e.target.value); setMemPage(1); }}
                              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                              data-testid="member-search-input"
                            />
                          </div>
                        )}
                        {pageItems.length > 0 ? (
                          <>
                            <div className="space-y-1">
                              {pageItems.map(m => (
                                <div key={m.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 group transition-colors">
                                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-[10px] font-bold">{m.name?.[0]}{m.surname?.[0]}</div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-800 truncate">{m.name} {m.surname}</p>
                                    <p className="text-[10px] text-slate-400">{m.email}</p>
                                  </div>
                                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[9px]">Uye</Badge>
                                  {canEdit && <button onClick={async () => {
                                    try {
                                      await organsAPI.removeApprovedMember(detail.id, m.id);
                                      toast.success('Uyelik kaldirildi');
                                      loadMembers(detail.id);
                                    } catch { toast.error('Hata'); }
                                  }} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 p-1 transition-opacity" data-testid={`remove-approved-${m.id}`}><UserMinus className="w-3 h-3" /></button>}
                                </div>
                              ))}
                            </div>
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100" data-testid="member-pagination">
                                <button
                                  onClick={() => setMemPage(p => Math.max(1, p - 1))}
                                  disabled={safePage <= 1}
                                  className="text-[10px] text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-slate-50"
                                  data-testid="member-pagination-prev"
                                >
                                  <ChevronLeft className="w-3 h-3 inline" /> Onceki
                                </button>
                                <span className="text-[10px] text-slate-500" data-testid="member-pagination-info">
                                  Sayfa <strong>{safePage}</strong> / {totalPages} <span className="text-slate-300">·</span> {filtered.length} uye
                                </span>
                                <button
                                  onClick={() => setMemPage(p => Math.min(totalPages, p + 1))}
                                  disabled={safePage >= totalPages}
                                  className="text-[10px] text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-slate-50"
                                  data-testid="member-pagination-next"
                                >
                                  Sonraki <ChevronRight className="w-3 h-3 inline" />
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="p-3 rounded-lg border border-dashed border-slate-200 text-center">
                            <p className="text-xs text-slate-400">{q ? 'Aramaya uygun uye bulunamadi' : 'Henuz onaylanmis uye yok'}</p>
                            {!q && <p className="text-[10px] text-slate-300 mt-0.5">Basvurular onaylandiginda veya manuel eklendiginde burada gorunur</p>}
                          </div>
                        )}
                      </div>
                      );
                    })()}
                  </div>
                )}

                {/* Children section */}
                {detail.children && detail.children.length > 0 && (
                  <div data-testid="children-section">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Alt Organlar ({detail.children.length})</h3>
                    <div className="space-y-1">
                      {detail.children.map(ch => {
                        const chCfg = TC[ch.type] || TC.meclis;
                        return (
                          <div key={ch.id} className="flex items-center gap-2.5 py-2 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => selectNode(ch)}>
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: chCfg.dot }} />
                            <span className="text-xs font-medium text-slate-700 flex-1 truncate">{ch.name}</span>
                            <Badge className={`${chCfg.lt} border text-[9px] px-1 py-0`}>{chCfg.label}</Badge>
                            {ch.children && ch.children.length > 0 && <span className="text-[9px] text-slate-400 bg-slate-100 rounded px-1">{ch.children.length}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick add child */}
                {canCreate && childTypes(detail.type).length > 0 && (
                  <div>
                    {childTypes(detail.type).map(ct => (
                      <Button key={ct} variant="outline" size="sm" className="text-xs mr-2 mt-1" onClick={() => openNew(ct, detail.id)} data-testid={`quick-add-${ct}`}>
                        <Plus className="w-3 h-3 mr-1" />{TC[ct]?.label || ct} Ekle
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="max-w-md" data-testid="organ-dialog">
          <DialogHeader><DialogTitle className="text-base">{editing ? 'Organ Duzenle' : 'Yeni Organ'}</DialogTitle><DialogDescription className="text-xs">{editing ? 'Bilgileri guncelleyin' : 'Hiyerarsiye yeni organ ekleyin'}</DialogDescription></DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="space-y-1"><Label className="text-xs">Tur</Label>
              {editing ? <Badge className={`${TC[form.type]?.lt || ''} border text-xs`}>{TC[form.type]?.label || form.type}</Badge> : (
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v, parent_id: '' })}><SelectTrigger className="h-9 text-xs" data-testid="form-type"><SelectValue /></SelectTrigger><SelectContent>{typeOpts.map(k => <SelectItem key={k} value={k} className="text-xs">{TC[k]?.label || k}</SelectItem>)}</SelectContent></Select>
              )}
            </div>
            {PM[form.type] && <div className="space-y-1"><Label className="text-xs">Ust Organ</Label><Select value={form.parent_id} onValueChange={v => setForm({ ...form, parent_id: v })}><SelectTrigger className="h-9 text-xs" data-testid="form-parent"><SelectValue placeholder="Sec" /></SelectTrigger><SelectContent>{parentOpts().map(o => <SelectItem key={o.id} value={o.id} className="text-xs">{o.name}</SelectItem>)}</SelectContent></Select></div>}
            <div className="space-y-1"><Label className="text-xs">Ad</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-xs" data-testid="form-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Aciklama</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="text-xs" data-testid="form-desc" /></div>
            {form.type === 'donem' && <div className="grid grid-cols-2 gap-2"><div className="space-y-1"><Label className="text-xs">Baslangic</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="h-9 text-xs" data-testid="form-start" /></div><div className="space-y-1"><Label className="text-xs">Bitis</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="h-9 text-xs" data-testid="form-end" /></div></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setDOpen(false)}>Iptal</Button>
            <Button size="sm" className="text-xs bg-slate-900" onClick={handleSave} disabled={saving} data-testid="form-save">{saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}{editing ? 'Guncelle' : 'Olustur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Donem Selector */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="export-donem-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Download className="w-4 h-4 text-emerald-600" />Meclisleri Excel'e Aktar</DialogTitle>
            <DialogDescription className="text-xs">Aktarilacak donemi seciniz. Secilen doneme ait tum meclisler, YK uyeleri ve CG uyeleri Excel'de ayri sayfalarda listelenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Donem *</Label>
            <select className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white" value={exportDonemId} onChange={e => setExportDonemId(e.target.value)} data-testid="export-donem-select">
              <option value="">-- Donem seciniz --</option>
              {allDonemler.map(d => (
                <option key={d.id} value={d.id}>{d.name}{d.is_active ? ' (Aktif)' : ''}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setExportOpen(false)}>Iptal</Button>
            <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700" disabled={!exportDonemId}
              onClick={() => { window.location.href = meclisBaskaniAPI.exportAll(exportDonemId); setExportOpen(false); }}
              data-testid="export-donem-submit">
              <Download className="w-3.5 h-3.5 mr-1" />Indir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Donem Dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-md" data-testid="copy-donem-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Copy className="w-4 h-4 text-indigo-600" />Donem Kopyala</DialogTitle>
            <DialogDescription className="text-xs">
              <b>{copySource?.name}</b> donemi tum alt organlari (Meclisler, Calisma Gruplari) ve opsiyonel uyeleriyle kopyalanacak. Yeni donem pasif olarak olusur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1">
              <Label className="text-xs">Yeni Donem Adi *</Label>
              <Input value={copyForm.new_name} onChange={e => setCopyForm({ ...copyForm, new_name: e.target.value })} className="h-9 text-xs" data-testid="copy-new-name" placeholder="Ornek: 2026-2030 Donemi" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Baslangic</Label>
                <Input type="date" value={copyForm.start_date} onChange={e => setCopyForm({ ...copyForm, start_date: e.target.value })} className="h-9 text-xs" data-testid="copy-start-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bitis</Label>
                <Input type="date" value={copyForm.end_date} onChange={e => setCopyForm({ ...copyForm, end_date: e.target.value })} className="h-9 text-xs" data-testid="copy-end-date" />
              </div>
            </div>
            <div className="space-y-2 pt-1 border-t border-slate-100">
              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Organ Yapisi</p>
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-members-toggle">
                <input type="checkbox" checked={copyForm.copy_members} onChange={e => setCopyForm({ ...copyForm, copy_members: e.target.checked })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span><b>Baskanlari ve YK uyelerini kopyala</b> (her organdaki baskan + members)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-approved-toggle">
                <input type="checkbox" checked={copyForm.copy_approved_members} onChange={e => setCopyForm({ ...copyForm, copy_approved_members: e.target.checked })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span><b>Onaylanmis Meclis/CG uyelerini kopyala</b> (basvuru kayitlari)</span>
              </label>
            </div>
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Donem Icerigi (opsiyonel)</p>

              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-meetings-toggle">
                <input type="checkbox" checked={copyForm.copy_meetings} onChange={e => setCopyForm({ ...copyForm, copy_meetings: e.target.checked, copy_meeting_attendees: e.target.checked ? copyForm.copy_meeting_attendees : false })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span>Toplantilari kopyala</span>
              </label>
              {copyForm.copy_meetings && (
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 ml-5" data-testid="copy-meeting-attendees-toggle">
                  <input type="checkbox" checked={copyForm.copy_meeting_attendees} onChange={e => setCopyForm({ ...copyForm, copy_meeting_attendees: e.target.checked })} className="w-3 h-3 rounded border-slate-300" />
                  <span>Katilimci listesini de kopyala</span>
                </label>
              )}

              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-announcements-toggle">
                <input type="checkbox" checked={copyForm.copy_announcements} onChange={e => setCopyForm({ ...copyForm, copy_announcements: e.target.checked, copy_announcements_active: e.target.checked ? copyForm.copy_announcements_active : false })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span>Duyurulari kopyala</span>
              </label>
              {copyForm.copy_announcements && (
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 ml-5" data-testid="copy-announcements-active-toggle">
                  <input type="checkbox" checked={copyForm.copy_announcements_active} onChange={e => setCopyForm({ ...copyForm, copy_announcements_active: e.target.checked })} className="w-3 h-3 rounded border-slate-300" />
                  <span>Yeniden aktif yayinla (yoksa pasif olarak kopyalanir)</span>
                </label>
              )}

              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-surveys-toggle">
                <input type="checkbox" checked={copyForm.copy_surveys} onChange={e => setCopyForm({ ...copyForm, copy_surveys: e.target.checked, copy_survey_responses: e.target.checked ? copyForm.copy_survey_responses : false })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span>Anketleri kopyala (taslak olarak)</span>
              </label>
              {copyForm.copy_surveys && (
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 ml-5" data-testid="copy-survey-responses-toggle">
                  <input type="checkbox" checked={copyForm.copy_survey_responses} onChange={e => setCopyForm({ ...copyForm, copy_survey_responses: e.target.checked })} className="w-3 h-3 rounded border-slate-300" />
                  <span>Anket yanitlarini da kopyala <span className="text-amber-600">(dikkat: veri tekrari)</span></span>
                </label>
              )}

              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-events-toggle">
                <input type="checkbox" checked={copyForm.copy_events} onChange={e => setCopyForm({ ...copyForm, copy_events: e.target.checked, copy_event_applications: e.target.checked ? copyForm.copy_event_applications : false })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span>Etkinlikleri kopyala</span>
              </label>
              {copyForm.copy_events && (
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 ml-5" data-testid="copy-event-applications-toggle">
                  <input type="checkbox" checked={copyForm.copy_event_applications} onChange={e => setCopyForm({ ...copyForm, copy_event_applications: e.target.checked })} className="w-3 h-3 rounded border-slate-300" />
                  <span>Etkinlik basvurularini da kopyala</span>
                </label>
              )}
            </div>
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="copy-activate-toggle">
                <input type="checkbox" checked={copyForm.activate} onChange={e => setCopyForm({ ...copyForm, activate: e.target.checked })} className="w-3.5 h-3.5 rounded border-slate-300" />
                <span><b>Yeni donemi hemen aktif yap</b> (mevcut aktif donem pasiflesir)</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setCopyOpen(false)} disabled={copySaving}>Iptal</Button>
            <Button size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700" onClick={handleCopyDonem} disabled={copySaving} data-testid="copy-donem-submit">
              {copySaving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Kopyalaniyor...</> : <><Copy className="w-3 h-3 mr-1" />Kopyala</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent className="max-w-sm" data-testid="user-search-dialog">
          <DialogHeader><DialogTitle className="text-base">{uMode === 'baskan' ? 'Baskan Ata' : uMode === 'approved' ? 'Uye Ekle' : 'YK Uyesi Ekle'}</DialogTitle><DialogDescription className="text-xs">Kullanici arayip secin</DialogDescription></DialogHeader>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={uSearch} onChange={e => searchUsers(e.target.value)} placeholder="Ad, soyad veya e-posta..." className="h-9 text-xs pl-9" data-testid="user-search-input" autoFocus />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-0.5">
            {uLoading && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
            {!uLoading && uResults.length === 0 && uSearch.length >= 2 && <p className="text-xs text-slate-400 text-center py-4">Sonuc bulunamadi</p>}
            {uResults.map(u => (
              <div key={u.id} className="flex items-center gap-2.5 py-2 px-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => uMode === 'baskan' ? assignBaskan(u.id) : uMode === 'approved' ? addApprovedMember(u.id) : addMember(u.id)} data-testid={`user-option-${u.id}`}>
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">{u.name?.[0]}{u.surname?.[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">{u.name} {u.surname}</p>
                  <p className="text-[10px] text-slate-400">{u.email}</p>
                </div>
                <Badge variant="outline" className="text-[9px]">{u.role}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
